/**
 * api-endpoints/endpoints.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SINGLE SOURCE OF TRUTH for every backend URL used in SK Upsell.
 *
 * Two backends:
 *   PLATFORM_API → skplatform-api   (the session flow — /v1/me, /v1/auth/refresh,
 *                  /v1/auth/logout — called DIRECTLY; no backbone hop. All auth
 *                  UI lives in SK-Platform-FE.)
 *   UPSELL_API   → sk-upsell-api    (rules, proposals, stats, settings,
 *                  presets, capabilities, preview, dpdp — the operator API)
 *
 * Rules:
 *   - Zero hardcoded URLs anywhere else in the codebase.
 *   - Base URLs come ONLY from process.env (injected by webpack DefinePlugin;
 *     see web/webpack.config.ts). To switch environments, set the env vars.
 * ──────────────────────────────────────────────────────────────────────────
 */

// ── Base URLs (build-time env, with local-dev fallbacks) ──────────────────
// SK-Platform API directly — the session flow does NOT proxy through a backbone.
export const PLATFORM_API = process.env.PLATFORM_API_URL || 'http://localhost:3013';
export const UPSELL_API = process.env.UPSELL_API_URL || 'http://localhost:3010';

// ── Versioned prefixes ────────────────────────────────────────────────────
const P = `${PLATFORM_API}/v1`; // Auth/identity — skplatform-api directly
const U = `${UPSELL_API}/v1`; // SK Upsell operator API

/* ═══════════════════════════════════════════════════════════════════════════
   PLATFORM SESSION — read-only + silent refresh.
   SK Upsell owns NO authentication UI. Login / signup / password / email-verify
   ALL live in SK-Platform-FE. The only platform calls this SPA makes are: a
   read-only identity probe (to gate the shell + show who's signed in), a SILENT
   token refresh (cookie rotation on a mid-session 401 — not an auth screen), and
   sign-out. Everything else is a redirect to the platform.
═══════════════════════════════════════════════════════════════════════════ */

export const Platform_Me_Url = `${P}/me`; // GET — identity probe (200 = in, 401 = out)
export const Platform_Refresh_Url = `${P}/auth/refresh`; // POST — silent access-token rotation (httpOnly cookie)
export const Platform_Logout_Url = `${P}/auth/logout`; // POST — ends the session (204)

/* ═══════════════════════════════════════════════════════════════════════════
   SK UPSELL — Rules
═══════════════════════════════════════════════════════════════════════════ */

export const Upsell_Rules_Url = `${U}/rules`; // GET (list) | POST (create)
export const Upsell_Rule_Url = (id: string) => `${U}/rules/${id}`; // GET | PATCH
export const Upsell_Rule_Activate_Url = (id: string) => `${U}/rules/${id}/activate`; // POST
export const Upsell_Rule_Deactivate_Url = (id: string) => `${U}/rules/${id}/deactivate`; // POST
export const Upsell_Rule_Delete_Url = (id: string) => `${U}/rules/${id}/delete`; // POST → soft-delete
export const Upsell_Rule_Restore_Url = (id: string) => `${U}/rules/${id}/restore`; // POST → back to Deactivated
export const Upsell_Rule_Preview_Url = `${U}/rules/preview`; // POST — dry-run preview

/* ═══════════════════════════════════════════════════════════════════════════
   SK UPSELL — Proposals & AI derivation
═══════════════════════════════════════════════════════════════════════════ */

export const Upsell_Events_Stream_Url = `${U}/events/stream`; // SSE — instant catalog-change push
export const Upsell_Proposals_Url = `${U}/proposals`; // GET ?status=pending
export const Upsell_Proposal_Approve_Url = (id: string) => `${U}/proposals/${id}/approve`; // POST
export const Upsell_Proposal_Reject_Url = (id: string) => `${U}/proposals/${id}/reject`; // POST
export const Upsell_Proposal_Restore_Url = (id: string) => `${U}/proposals/${id}/restore`; // POST → back to pending
export const Upsell_Proposal_Delete_Url = (id: string) => `${U}/proposals/${id}/delete`; // POST → soft-delete
export const Upsell_Derivation_Run_Url = `${U}/derivation/run`; // POST → { run_id, proposals_count }

/* ═══════════════════════════════════════════════════════════════════════════
   SK UPSELL — Capabilities, Stats, Settings
═══════════════════════════════════════════════════════════════════════════ */

export const Upsell_Capabilities_Url = `${U}/capabilities`; // GET — strategy/motion gating
export const Upsell_Stats_Url = `${U}/stats`; // GET ?range=&sort=
export const Upsell_Settings_Url = `${U}/settings`; // GET | PATCH

/* ═══════════════════════════════════════════════════════════════════════════
   SK UPSELL — Presets
═══════════════════════════════════════════════════════════════════════════ */

export const Upsell_Presets_Url = `${U}/presets`; // GET — list bundles
export const Upsell_Preset_Url = (key: string) => `${U}/presets/${key}`; // GET — manifest
export const Upsell_Preset_Install_Url = (key: string) => `${U}/presets/${key}/install`; // POST

/* ═══════════════════════════════════════════════════════════════════════════
   SK UPSELL — DPDP / privacy + health
═══════════════════════════════════════════════════════════════════════════ */

export const Upsell_Dpdp_Erase_Url = `${U}/dpdp/erase`; // POST { contact_id }
export const Upsell_Health_Url = `${UPSELL_API}/health`; // GET — unauthenticated
